import type { ContactJson } from 'app/types/entities'
import type { TreatmentCard } from 'app/types/treatment'
import type { CardDensity, ListCardConfig } from 'lib/listCardConfig'
import type { CardField } from 'lib/listCardFields'

export type CardFieldId = CardField[`id`]

export type ContactCardSubject = { kind: `contact`; contact: ContactJson }
export type CaseCardSubject = { kind: `case`; card: TreatmentCard }

/**
 * What a card can show about a contact. The first four are a custom list's card; the rest are
 * the Action Center card's, and read the facts `contactCardFacts` derives from the queue.
 */
export type ContactCardSection =
  | { kind: `identity`; subtitle?: CardFieldId; showAvatar: boolean; showAge: boolean }
  | { kind: `hero`; field: CardFieldId }
  | { kind: `meta`; fields: CardFieldId[] }
  /** `context`: the board's pre-fetched map (custom lists). `lazy`: one read per card (system boards). */
  | { kind: `comms`; source: `context` | `lazy` }
  | { kind: `nameRow` }
  | { kind: `cue` }
  | { kind: `tags` }
  | { kind: `details` }
  | { kind: `chips` }
  /** `variant` is layout only and must match the queue's family: `BoardCard` picks `LEAD_CARD` on
   * the same `def.family === 'lead'` test that `contactCardFacts` returns as `lead`. */
  | { kind: `bottom`; variant: `hygiene` | `lead` }

/** What a card can show about a treatment case. */
export type CaseCardSection =
  | { kind: `caseHeader` }
  | { kind: `reasonBadge` }
  | { kind: `procedureGroups` }
  | { kind: `splitBar` }

/**
 * Padding and liftability differ between the card families, so they live here and never as a
 * default in `RecipeCard`. `body` wraps every section in one div inside the frame.
 */
export type CardFrame = { className: string; liftable: boolean; body?: string }

export type ContactCardRecipe = {
  subject: `contact`
  density: CardDensity
  frame: CardFrame
  sections: ContactCardSection[]
}

export type CaseCardRecipe = {
  subject: `case`
  density: CardDensity
  frame: CardFrame
  sections: CaseCardSection[]
}

/** A recipe with the subject it draws, paired so a case recipe is never handed a contact. */
export type RecipeAndSubject =
  | { recipe: ContactCardRecipe; subject: ContactCardSubject }
  | { recipe: CaseCardRecipe; subject: CaseCardSubject }

/** A custom list's saved card configuration, as the recipe `RecipeCard` draws. */
export function recipeFromConfig(config: ListCardConfig): ContactCardRecipe {
  const { slots } = config
  const sections: ContactCardSection[] = [
    { kind: `identity`, subtitle: slots.subtitle ?? undefined, showAvatar: config.showAvatar, showAge: config.showAge },
  ]
  if (slots.hero) sections.push({ kind: `hero`, field: slots.hero })
  if (slots.meta.length > 0) sections.push({ kind: `meta`, fields: slots.meta })
  if (config.showCommunications) sections.push({ kind: `comms`, source: `context` })
  return {
    subject: `contact`,
    density: config.density,
    frame: {
      // The communications strip runs edge to edge, so the body pads its own regions and the
      // frame clips to its corners. The top padding lives on a div inside the link, not here:
      // with an href the frame's own root carries no click handler (the link owns opening), so
      // padding on the root would be a strip of the card that does nothing when clicked. The
      // whole face of the card is the link.
      className: `relative overflow-hidden`,
      liftable: true,
      body: config.density === `compact` ? `pt-2` : `pt-3`,
    },
    sections,
  }
}
